import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { theme, breakpoint } from "@utilities";
import {
    hasConsentBeenRecorded,
    acceptAllCookies,
    acceptNecessaryOnly,
} from "src/Utilities/cookieConsent";
import { CookiePreferencesModal } from "src/Components/CookieConsent/CookiePreferencesModal";

/**
 * CookieConsentBanner Component
 *
 * Displays a cookie consent banner at the bottom-right of the screen
 * when the user hasn't made a consent choice yet.
 *
 * Features:
 * - "Accept all" button - accepts all cookie categories
 * - "Accept necessary" button - accepts only necessary cookies
 * - "Cookie Settings" link - opens detailed preferences modal
 */
export const CookieConsentBanner: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);

    useEffect(() => {
        // Only show if consent hasn't been recorded yet
        if (!hasConsentBeenRecorded()) {
            // Small delay to avoid flash on page load
            const timer = setTimeout(() => setIsVisible(true), 500);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAcceptAll = () => {
        acceptAllCookies();
        setIsVisible(false);
    };

    const handleAcceptNecessary = () => {
        acceptNecessaryOnly();
        setIsVisible(false);
    };

    const handleOpenSettings = () => {
        setShowPreferences(true);
    };

    const handleClosePreferences = () => {
        setShowPreferences(false);
    };

    const handleSavePreferences = () => {
        setShowPreferences(false);
        setIsVisible(false);
    };

    if (!isVisible && !showPreferences) {
        return null;
    }

    return (
        <>
            {isVisible && (
                <BannerContainer>
                    <BannerContent>
                        <Title>Cookie Consent</Title>
                        <Description>
                            Hi, we use cookies to ensure the website's proper operation, to analyze
                            traffic and performance, and to provide social media features.{" "}
                            <SettingsLink onClick={handleOpenSettings}>
                                Cookie Settings
                            </SettingsLink>
                        </Description>
                        <ButtonContainer>
                            <AcceptAllButton onClick={handleAcceptAll}>Accept all</AcceptAllButton>
                            <AcceptNecessaryButton onClick={handleAcceptNecessary}>
                                Accept necessary
                            </AcceptNecessaryButton>
                        </ButtonContainer>
                    </BannerContent>
                </BannerContainer>
            )}

            {showPreferences && (
                <CookiePreferencesModal
                    onClose={handleClosePreferences}
                    onSave={handleSavePreferences}
                />
            )}
        </>
    );
};

const BannerContainer = styled.div`
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
    max-width: 400px;
    width: calc(100% - 40px);
    background: ${theme.colors.white};
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease-out;

    ${breakpoint.breakOnlyMobile`
        bottom: 10px;
        right: 10px;
        left: 10px;
        width: auto;
        max-width: none;
    `}

    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const BannerContent = styled.div`
    padding: 20px;
`;

const Title = styled.h3`
    font-family: ${theme.fonts.primary};
    font-size: 18px;
    font-weight: 700;
    color: ${theme.colors.gold ? theme.colors.black : theme.colors.highlight};
    margin: 0 0 12px 0;
`;

const Description = styled.p`
    font-family: ${theme.fonts.primary};
    font-size: 14px;
    line-height: 1.5;
    color: ${theme.colors.gray_2};
    margin: 0 0 16px 0;
`;

const SettingsLink = styled.span`
    color: ${theme.colors.highlight};
    text-decoration: underline;
    cursor: pointer;
    font-weight: 500;

    &:hover {
        text-decoration: none;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const BaseButton = styled.button`
    font-family: ${theme.fonts.primary};
    font-size: 14px;
    font-weight: 600;
    padding: 12px 24px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex: 1;
    min-width: 120px;

    &:focus {
        outline: 2px solid ${theme.colors.highlight};
        outline-offset: 2px;
    }
`;

const AcceptAllButton = styled(BaseButton)`
    background: ${theme.colors.gold || theme.colors.highlight};
    color: ${theme.colors.gold ? theme.colors.black : theme.colors.white};
    border: none;

    &:hover {
        opacity: 0.9;
    }
`;

const AcceptNecessaryButton = styled(BaseButton)`
    background: ${theme.colors.white};
    color: ${theme.colors.gray_2};
    border: 2px solid ${theme.colors.gray_5};

    &:hover {
        background: ${theme.colors.gray_6};
    }
`;

export default CookieConsentBanner;
